import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api, { getTwitchAuthorizeUrl } from '../api';

interface TwitchUser {
  id: string;
  login: string;
  displayName: string;
  profileImageUrl?: string;
}

interface TwitchAuthContextValue {
  user: TwitchUser | null;
  token: string | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
  setSession: (token: string, user: TwitchUser) => void;
}

const TwitchAuthContext = createContext<TwitchAuthContextValue | undefined>(undefined);
const AUTH_MESSAGE = 'EUGENIAL_TWITCH_AUTH';

export const TwitchAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<TwitchUser | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('eugenial_twitch_token'));
  const [loading, setLoading] = useState(true);

  const setSession = useCallback((newToken: string, newUser: TwitchUser) => {
    localStorage.setItem('eugenial_twitch_token', newToken);
    setToken(newToken);
    setUser(newUser);
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get('/twitch/auth/me');
        setUser(data);
      } catch {
        localStorage.removeItem('eugenial_twitch_token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== AUTH_MESSAGE) return;
      if (event.data.token && event.data.user) {
        setSession(event.data.token, event.data.user);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [setSession]);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    login: () => {
      try {
        window.location.assign(getTwitchAuthorizeUrl());
      } catch {
        alert('Error de configuración: falta REACT_APP_TWITCH_CLIENT_ID en el build.');
      }
    },
    logout: () => {
      localStorage.removeItem('eugenial_twitch_token');
      setToken(null);
      setUser(null);
    },
    setSession,
  }), [user, token, loading, setSession]);

  return (
    <TwitchAuthContext.Provider value={value}>{children}</TwitchAuthContext.Provider>
  );
};

export function useTwitchAuth() {
  const ctx = useContext(TwitchAuthContext);
  if (!ctx) throw new Error('useTwitchAuth debe usarse dentro de TwitchAuthProvider');
  return ctx;
}

export { AUTH_MESSAGE };
